// src/components/hall-prices/HallPricesCanvas.tsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import {
    HallScheme,
    SchemeSector,
    SchemeSeat,
    SchemeStandingPlace,
    SchemeTextLabel,
    SchemeShape,
    ScaleValue,
} from "../hall-schemes/HallSchemeEditorCanvas";
import {
    HallPriceCategoryItem,
    HallSeatPriceItem,
    HallSeatPriceBulkItem,
} from "../../services/types/hallPrices.types";

type CanvasPoint = { x: number; y: number };

type HallPricesCanvasProps = {
    scale: ScaleValue;
    scheme: HallScheme | null;

    sectors: SchemeSector[];
    seats: SchemeSeat[];
    standingPlaces: SchemeStandingPlace[];
    selectionTool: string;
    textLabels: SchemeTextLabel[];
    shapes: SchemeShape[];

    priceCategories: HallPriceCategoryItem[];
    seatPrices: HallSeatPriceItem[];

    selectedSeatIds: string[];
    onSelectedSeatIdsChange: (ids: string[]) => void;

    selectedStandingPlaceId: string | null;
    onSelectedStandingPlaceIdChange: (id: string | null) => void;
    /** Клик прямо по стоячей зоне на схеме — сразу открывает выбор количества для заказа. */
    onStandingPlaceOrderClick?: (id: string) => void;

    onSeatPricesChange: (items: HallSeatPriceBulkItem[]) => void;

    /** id элементов схемы (места/стоячие зоны), которые заблокированы для выделения (заняты заказом или адм. бронью) */
    blockedElementIds?: Set<string>;

    /** Возвращает кешированную информацию о заказе / адм. брони, которая заняла место. */
    getBlockedInfo?: (elementId: string) => BlockedSeatInfo | null;

    /** Вызывается при наведении на занятое место — триггер ленивой загрузки деталей. */
    onBlockedHover?: (elementId: string) => void;
};

export type BlockedSeatInfo = {
    kind: "order" | "admin_reservation" | "partner";
    loading?: boolean;
    error?: string;
    orderNumber?: string;
    customerName?: string;
    customerEmail?: string;
    createdByName?: string;
    createdByRole?: string;
    apiDistributorName?: string;
    isApiOrder?: boolean;
    isAgent?: boolean;
    isPaid?: boolean;
    isPaidByDistributor?: boolean;
    isDeleted?: boolean;
    // причина блокировки у партнёра (продано/забронировано/нет цены)
    message?: string;
    reservationName?: string;
    reservationCreatorName?: string;
    reservationUserName?: string;
};

const GRID_CELLS = 200;
const CELL_SIZE = 24;
const BOARD_SIZE = GRID_CELLS * CELL_SIZE;
const SEAT_SIZE = 18;
const LASSO_MIN_DISTANCE = 8;

const getDistance = (a: CanvasPoint, b: CanvasPoint) => {
    return Math.hypot(a.x - b.x, a.y - b.y);
};

const isPointInPolygon = (point: CanvasPoint, polygon: CanvasPoint[]) => {
    if (polygon.length < 3) {
        return false;
    }

    let isInside = false;

    for (let i = 0, j = polygon.length - 1; i < polygon.length; j = i++) {
        const xi = polygon[i].x;
        const yi = polygon[i].y;
        const xj = polygon[j].x;
        const yj = polygon[j].y;

        const intersects =
            yi > point.y !== yj > point.y &&
            point.x < ((xj - xi) * (point.y - yi)) / ((yj - yi) || 0.00001) + xi;

        if (intersects) {
            isInside = !isInside;
        }
    }

    return isInside;
};

const HallPricesCanvas: React.FC<HallPricesCanvasProps> = ({
                                                               scale,
                                                               scheme,
                                                               sectors,
                                                               seats,
                                                               standingPlaces,
                                                               selectionTool,
                                                               textLabels,
                                                               shapes,
                                                               priceCategories,
                                                               seatPrices,
                                                               selectedSeatIds,
                                                               onSelectedSeatIdsChange,
                                                               selectedStandingPlaceId,
                                                               onSelectedStandingPlaceIdChange,
                                                               onStandingPlaceOrderClick,
                                                               blockedElementIds,
                                                               getBlockedInfo,
                                                               onBlockedHover,
                                                           }) => {
    const isElementBlocked = (elementId: string) =>
        Boolean(blockedElementIds && blockedElementIds.has(elementId));

    const renderBlockedTooltipRows = (info: BlockedSeatInfo | null | undefined) => {
        if (!info) return null;
        if (info.loading) {
            return <span style={{ opacity: 0.85 }}>Загружаю данные…</span>;
        }
        if (info.error) {
            return <span style={{ color: "#fecaca" }}>{info.error}</span>;
        }
        if (info.kind === "order") {
            return (
                <>
                    <span style={{ fontWeight: 600 }}>
                        Заказ №{info.orderNumber ?? "—"}
                        {info.isDeleted ? " (удалён)" : ""}
                    </span>
                    <span>Клиент: {info.customerName || "—"}</span>
                    {info.isApiOrder && info.apiDistributorName ? (
                        <span>Оформил: {info.apiDistributorName} (API)</span>
                    ) : info.createdByName ? (
                        <span>
                            Оформил: {info.createdByName}
                            {info.createdByRole ? ` (${info.createdByRole})` : ""}
                        </span>
                    ) : null}
                    <span style={{ opacity: 0.85 }}>
                        {info.isPaid
                            ? "Оплачен"
                            : info.isPaidByDistributor
                                ? "Оплачен у распространителя"
                                : info.isAgent
                                    ? "Агентский, ожидает оплаты"
                                    : "Не оплачен"}
                    </span>
                </>
            );
        }
        if (info.kind === "partner") {
            return <span>{info.message || "Место недоступно у партнёра"}</span>;
        }
        // admin_reservation
        return (
            <>
                <span style={{ fontWeight: 600 }}>
                    Адм. бронь: {info.reservationName || "—"}
                </span>
                {info.reservationUserName ? (
                    <span>Клиент: {info.reservationUserName}</span>
                ) : null}
                {info.reservationCreatorName ? (
                    <span>Оформил: {info.reservationCreatorName}</span>
                ) : null}
            </>
        );
    };
    const scrollAreaRef = useRef<HTMLDivElement | null>(null);
    const gridSurfaceRef = useRef<HTMLDivElement | null>(null);

    const [isPointerDown, setIsPointerDown] = useState(false);
    const [selectionStart, setSelectionStart] = useState<CanvasPoint | null>(null);
    const [selectionEnd, setSelectionEnd] = useState<CanvasPoint | null>(null);
    const [hoveredSeatId, setHoveredSeatId] = useState<string | null>(null);
    const [hoveredStandingPlaceId, setHoveredStandingPlaceId] = useState<string | null>(null);
    const [lassoPoints, setLassoPoints] = useState<CanvasPoint[]>([]);

    const sectorById = useMemo(
        () => new Map<string, SchemeSector>(sectors.map((item) => [item.id, item])),
        [sectors],
    );

    const gridStyle = useMemo(() => {
        return {
            width: `${BOARD_SIZE}px`,
            height: `${BOARD_SIZE}px`,
        };
    }, []);

    useEffect(() => {
        const area = scrollAreaRef.current;

        if (!area) {
            return;
        }

        const centerX = (BOARD_SIZE * scale - area.clientWidth) / 2;
        const centerY = (BOARD_SIZE * scale - area.clientHeight) / 2;

        area.scrollLeft = Math.max(0, centerX);
        area.scrollTop = Math.max(0, centerY);
    }, [scale]);

    const priceCategoryById = useMemo(() => {
        return new Map<string, HallPriceCategoryItem>(
            priceCategories.map((item) => [item.id, item]),
        );
    }, [priceCategories]);

    const safeSeatPrices = useMemo(() => {
        return Array.isArray(seatPrices) ? seatPrices : [];
    }, [seatPrices]);

    const seatPriceByElementId = useMemo(
        () =>
            new Map<string, HallSeatPriceItem>(
                safeSeatPrices.map((item) => [item.hall_scheme_element_id, item]),
            ),
        [safeSeatPrices],
    );

    const getPriceInfo = (elementId: string) => {
        const seatPrice = seatPriceByElementId.get(elementId);

        if (!seatPrice) {
            return null;
        }

        const category = seatPrice.price_id
            ? priceCategoryById.get(seatPrice.price_id) ?? null
            : null;

        return {
            seatPrice,
            category,
        };
    };

    const getPointerPosition = (event: React.PointerEvent<HTMLElement>) => {
        const surface = gridSurfaceRef.current;

        if (!surface) {
            return null;
        }

        const rect = surface.getBoundingClientRect();
        const x = (event.clientX - rect.left) / scale;
        const y = (event.clientY - rect.top) / scale;

        return {
            x: Math.max(0, Math.min(BOARD_SIZE, x)),
            y: Math.max(0, Math.min(BOARD_SIZE, y)),
        };
    };

    const getCellFromPointer = (event: React.PointerEvent<HTMLElement>) => {
        const point = getPointerPosition(event);

        if (!point) {
            return null;
        }

        const col = Math.max(0, Math.min(GRID_CELLS - 1, Math.floor(point.x / CELL_SIZE)));
        const row = Math.max(0, Math.min(GRID_CELLS - 1, Math.floor(point.y / CELL_SIZE)));

        return { col, row };
    };

    const resetSelectionDraft = () => {
        setSelectionStart(null);
        setSelectionEnd(null);
        setLassoPoints([]);
        setIsPointerDown(false);
    };

    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        if (event.button !== 0) {
            return;
        }

        const cell = getCellFromPointer(event);

        if (!cell) {
            return;
        }

        const target = event.target as HTMLElement;

        if (
            target.closest(".scheme-editor-seat-node") ||
            target.closest(".scheme-editor-standing-place")
        ) {
            return;
        }

        const point = getPointerPosition(event);

        if (!point) {
            return;
        }

        setIsPointerDown(true);
        // Не сбрасываем ни selectedSeatIds, ни selectedStandingPlaceId — выделение в режимах
        // "select"/"lasso" должно добавляться к уже выбранным местам, а стоячая зона может
        // оставаться выбранной одновременно с обычными местами (для смешанного заказа).
        // Сброс делается отдельной кнопкой в тулбаре или повторным кликом по конкретному месту.

        if (selectionTool === "lasso") {
            setSelectionStart(null);
            setSelectionEnd(null);
            setLassoPoints([point]);
            return;
        }

        setLassoPoints([]);
        setSelectionStart(point);
        setSelectionEnd(point);
    };

    const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isPointerDown) {
            return;
        }

        const point = getPointerPosition(event);

        if (!point) {
            return;
        }

        if (selectionTool === "lasso") {
            setLassoPoints((prev) => {
                if (prev.length === 0) {
                    return [point];
                }

                const lastPoint = prev[prev.length - 1];

                if (getDistance(lastPoint, point) < LASSO_MIN_DISTANCE) {
                    return prev;
                }

                return [...prev, point];
            });

            return;
        }

        if (!selectionStart) {
            return;
        }

        setSelectionEnd(point);
    };

    const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
        if (!isPointerDown) {
            return;
        }

        const point = getPointerPosition(event);

        if (!point) {
            resetSelectionDraft();
            return;
        }

        if (selectionTool === "lasso") {
            const polygon = (() => {
                if (lassoPoints.length === 0) {
                    return [];
                }

                const lastPoint = lassoPoints[lassoPoints.length - 1];
                const nextPoints =
                    getDistance(lastPoint, point) >= 1 ? [...lassoPoints, point] : lassoPoints;

                return nextPoints;
            })();

            if (polygon.length >= 3) {
                const lassoSeatIds = seats
                    .filter((seat) => {
                        if (isElementBlocked(seat.id)) {
                            return false;
                        }
                        const seatCenter = {
                            x: seat.x + SEAT_SIZE / 2,
                            y: seat.y + SEAT_SIZE / 2,
                        };

                        return isPointInPolygon(seatCenter, polygon);
                    })
                    .map((seat) => seat.id);

                // Добавляем новые места к уже выбранным, не сбрасывая предыдущий выбор.
                const merged = new Set<string>(selectedSeatIds);
                lassoSeatIds.forEach((id) => merged.add(id));
                onSelectedSeatIdsChange(Array.from(merged));
            }

            resetSelectionDraft();
            return;
        }

        if (selectionStart) {
            const left = Math.min(selectionStart.x, point.x);
            const right = Math.max(selectionStart.x, point.x);
            const top = Math.min(selectionStart.y, point.y);
            const bottom = Math.max(selectionStart.y, point.y);

            const rectSeatIds = seats
                .filter((seat) => {
                    if (isElementBlocked(seat.id)) {
                        return false;
                    }
                    const seatCenterX = seat.x + SEAT_SIZE / 2;
                    const seatCenterY = seat.y + SEAT_SIZE / 2;

                    return (
                        seatCenterX >= left &&
                        seatCenterX <= right &&
                        seatCenterY >= top &&
                        seatCenterY <= bottom
                    );
                })
                .map((seat) => seat.id);

            // Прямоугольное выделение тоже добавляет к существующему выбору,
            // не сбрасывая ранее выбранные места.
            const merged = new Set<string>(selectedSeatIds);
            rectSeatIds.forEach((id) => merged.add(id));
            onSelectedSeatIdsChange(Array.from(merged));
        }

        resetSelectionDraft();
    };

    const handleSeatClick = (
        event: React.MouseEvent<HTMLButtonElement>,
        seatId: string,
    ) => {
        event.stopPropagation();
        // Не сбрасываем выбранную стоячую зону — обычные места и стоячая зона теперь
        // могут быть выбраны одновременно, чтобы попасть в один заказ.

        // Заблокированные места (заказ/адм. бронь) нельзя выделять.
        if (isElementBlocked(seatId)) {
            return;
        }

        const isSelected = selectedSeatIds.includes(seatId);

        // Клик по месту всегда работает как toggle: если место уже выбрано — убираем его,
        // иначе добавляем к текущему выбору (не сбрасывая остальные).
        if (isSelected) {
            onSelectedSeatIdsChange(selectedSeatIds.filter((id) => id !== seatId));
            return;
        }

        onSelectedSeatIdsChange([...selectedSeatIds, seatId]);
    };

    const selectionRect = useMemo(() => {
        if (selectionTool !== "select" || !selectionStart || !selectionEnd) {
            return null;
        }

        return {
            left: Math.min(selectionStart.x, selectionEnd.x),
            top: Math.min(selectionStart.y, selectionEnd.y),
            width: Math.abs(selectionStart.x - selectionEnd.x),
            height: Math.abs(selectionStart.y - selectionEnd.y),
        };
    }, [selectionTool, selectionStart, selectionEnd]);

    const lassoPolygonPoints = useMemo(() => {
        if (selectionTool !== "lasso" || lassoPoints.length < 2) {
            return "";
        }

        return lassoPoints.map((point) => `${point.x},${point.y}`).join(" ");
    }, [selectionTool, lassoPoints]);

    return (
        <div
            ref={scrollAreaRef}
            className="scheme-editor-canvas-area"
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={handlePointerUp}
            onPointerLeave={resetSelectionDraft}
            aria-label={scheme?.name ?? "Схема зала"}
        >
            <div
                className="scheme-editor-grid-scale-layer"
                style={{ transform: `scale(${scale})` }}
            >
                <div
                    ref={gridSurfaceRef}
                    className="scheme-editor-grid-surface"
                    style={gridStyle}
                >
                    {shapes.map((shape) => (
                        <div
                            key={shape.id}
                            className="scheme-editor-shape"
                            style={{
                                left: `${shape.x}px`,
                                top: `${shape.y}px`,
                                width: `${shape.width}px`,
                                height: `${shape.height}px`,
                                borderRadius: shape.isOval ? "9999px" : `${shape.borderRadius}px`,
                            }}
                            aria-hidden="true"
                        />
                    ))}

                    {seats.map((seat) => {
                        const isSelected = selectedSeatIds.includes(seat.id);
                        const seatSector = seat.sectorId ? sectorById.get(seat.sectorId) ?? null : null;
                        const priceInfo = getPriceInfo(seat.id);
                        const isHovered = hoveredSeatId === seat.id;
                        const isBlocked = isElementBlocked(seat.id);

                        return (
                            <button
                                key={seat.id}
                                type="button"
                                className={`scheme-editor-seat-node ${
                                    isSelected ? "scheme-editor-seat-node-active" : ""
                                } ${isBlocked ? "scheme-editor-seat-node-blocked" : ""}`}
                                style={{
                                    left: `${seat.x}px`,
                                    top: `${seat.y}px`,
                                    background: isBlocked
                                        ? "#8f8f8f"
                                        : priceInfo?.category?.color ?? "#f8fafc",
                                    color: isBlocked ? "#64748b" : "#0f172a",
                                    // opacity: isBlocked ? 0.7 : undefined,
                                    cursor: isBlocked ? "not-allowed" : undefined,
                                }}
                                onMouseEnter={() => {
                                    setHoveredSeatId(seat.id);
                                    if (isBlocked && onBlockedHover) {
                                        onBlockedHover(seat.id);
                                    }
                                }}
                                onMouseLeave={() => setHoveredSeatId(null)}
                                onClick={(event) => handleSeatClick(event, seat.id)}
                                aria-disabled={isBlocked || undefined}
                            >
                                <span className="scheme-editor-seat-node-label">
                                    {seat.seatNumber}
                                </span>

                                {isHovered ? (
                                    <span className="scheme-editor-seat-tooltip">
                                        <span>Место: {seat.seatNumber}</span>
                                        <span>Ряд: {seat.rowNumber}</span>
                                        <span>Сектор: {seatSector?.name ?? "—"}</span>
                                        <span>
                                            Цена:{" "}
                                           {priceInfo?.category?.price
                                               ? `${Math.round(Number(priceInfo.category.price))} €`
                                               : "—"}
                                        </span>
                                        {isBlocked ? (
                                            <>
                                                <span
                                                    style={{
                                                        marginTop: 4,
                                                        paddingTop: 4,
                                                        borderTop:
                                                            "1px solid rgba(148,163,184,0.35)",
                                                    }}
                                                />
                                                {renderBlockedTooltipRows(
                                                    getBlockedInfo ? getBlockedInfo(seat.id) : null,
                                                )}
                                            </>
                                        ) : null}
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}

                    {standingPlaces.map((place) => {
                        const isSelected = selectedStandingPlaceId === place.id;
                        const priceInfo = getPriceInfo(place.id);
                        const isBlocked = isElementBlocked(place.id);

                        return (
                            <button
                                key={place.id}
                                type="button"
                                className={`scheme-editor-standing-place ${
                                    isSelected ? "scheme-editor-standing-place-active" : ""
                                } ${isBlocked ? "scheme-editor-standing-place-blocked" : ""}`}
                                style={{
                                    left: `${place.x}px`,
                                    top: `${place.y}px`,
                                    width: `${place.width}px`,
                                    height: `${place.height}px`,
                                    background: isBlocked
                                        ? "#cbd5e1"
                                        : priceInfo?.category?.color ?? "rgba(248, 250, 252, 0.9)",
                                    color: isBlocked
                                        ? "#64748b"
                                        : priceInfo?.category?.color
                                            ? "#ffffff"
                                            : "#0f172a",
                                    opacity: isBlocked ? 0.7 : undefined,
                                    cursor: isBlocked ? "not-allowed" : undefined,
                                }}
                                onMouseEnter={() => {
                                    setHoveredStandingPlaceId(place.id);
                                    if (isBlocked && onBlockedHover) {
                                        onBlockedHover(place.id);
                                    }
                                }}
                                onMouseLeave={() => setHoveredStandingPlaceId(null)}
                                onClick={(event) => {
                                    event.stopPropagation();
                                    if (isBlocked) return;
                                    // Не сбрасываем выбранные обычные места — стоячая зона выбирается
                                    // дополнительно к ним, чтобы можно было оформить смешанный заказ.
                                    // Клик по зоне сразу открывает выбор количества для заказа (если обработчик передан).
                                    onSelectedStandingPlaceIdChange(place.id);
                                    onStandingPlaceOrderClick?.(place.id);
                                }}
                                aria-disabled={isBlocked || undefined}
                            >
                               <span
                                   className="scheme-editor-standing-place-label"
                                   style={{
                                       display: "flex",
                                       flexDirection: "column",
                                       alignItems: "center",
                                       justifyContent: "center",
                                       textAlign: "center",
                                       lineHeight: 1.2,
                                       gap: "2px",
                                       whiteSpace: "pre-line",
                                   }}
                               >
                                    <span>{place.text || "Стоячая зона"}</span>

                                                                   {priceInfo?.seatPrice?.count != null ? (
                                                                       <span style={{ fontSize: "12px", opacity: 0.95 }}>
                                            Кол-во: {priceInfo.seatPrice.count}
                                        </span>
                                                                   ) : null}

                                                                   <span style={{ fontSize: "12px", opacity: 0.95 }}>
                                        {priceInfo?.category?.price
                                            ? `${Math.round(Number(priceInfo.category.price))} €`
                                            : "—"}
                                    </span>
                                </span>

                                {hoveredStandingPlaceId === place.id ? (
                                    <span className="scheme-editor-seat-tooltip">
                                        <span>Стоячая зона: {place.text}</span>
                                        <span>
                                            Цена:{" "}
                                            {priceInfo?.category?.price
                                                ? `${Math.round(Number(priceInfo.category.price))} €`
                                                : "—"}
                                        </span>
                                        {priceInfo?.seatPrice?.count != null ? (
                                            <span>Количество: {priceInfo.seatPrice.count}</span>
                                        ) : null}
                                        {isBlocked ? (
                                            <>
                                                <span
                                                    style={{
                                                        marginTop: 4,
                                                        paddingTop: 4,
                                                        borderTop:
                                                            "1px solid rgba(148,163,184,0.35)",
                                                    }}
                                                />
                                                {renderBlockedTooltipRows(
                                                    getBlockedInfo
                                                        ? getBlockedInfo(place.id)
                                                        : null,
                                                )}
                                            </>
                                        ) : null}
                                    </span>
                                ) : null}
                            </button>
                        );
                    })}

                    {textLabels.map((label) => (
                        <div
                            key={label.id}
                            className="scheme-editor-text-label"
                            style={{
                                left: `${label.x}px`,
                                top: `${label.y}px`,
                                width: `${label.width}px`,
                                height: `${label.height}px`,
                                border: "none",
                                background: "transparent",
                                padding: 0,
                                borderRadius: 0,
                                boxShadow: "none",
                                overflow: "visible",
                                justifyContent: "center",
                                alignItems: "center",
                            }}
                            aria-hidden="true"
                        >
                            <span
                                className="scheme-editor-text-label-text"
                                style={{
                                    whiteSpace: "nowrap",
                                    background: "transparent",
                                    border: "none",
                                    display: "inline-block",
                                    textAlign: "center",
                                }}
                            >
                                {label.text}
                            </span>
                        </div>
                    ))}

                    {selectionRect ? (
                        <div
                            className="scheme-editor-selection-rect"
                            style={{
                                left: `${selectionRect.left}px`,
                                top: `${selectionRect.top}px`,
                                width: `${selectionRect.width}px`,
                                height: `${selectionRect.height}px`,
                            }}
                        />
                    ) : null}

                    {lassoPolygonPoints ? (
                        <svg
                            className="scheme-editor-selection-lasso"
                            style={{
                                position: "absolute",
                                inset: 0,
                                width: `${BOARD_SIZE}px`,
                                height: `${BOARD_SIZE}px`,
                                pointerEvents: "none",
                                overflow: "visible",
                            }}
                            viewBox={`0 0 ${BOARD_SIZE} ${BOARD_SIZE}`}
                        >
                            <polygon
                                points={lassoPolygonPoints}
                                fill="rgba(109, 124, 255, 0.14)"
                                stroke="rgba(109, 124, 255, 0.95)"
                                strokeWidth={2}
                                strokeLinejoin="round"
                            />
                        </svg>
                    ) : null}
                </div>
            </div>
        </div>
    );
};

export default HallPricesCanvas;
